import { encode } from "querystring"

import { formatISO, isValid, parseISO } from "date-fns"
import { Location } from "gen/company/models_pb"
import _ from "lodash"
import Router, { NextRouter } from "next/router"

import { fixParsedId, stringify } from "lib/stringify"

export function pushParam(
  router: NextRouter,
  paramName: string,
  value: string
) {
  if (value) {
    router.query = {
      ...router.query,
      [paramName]: value,
    }
    router.push(router, undefined, { shallow: true })
    return
  }

  if (router.query[paramName] === undefined) {
    return
  }

  delete router.query[paramName]
  router.push(router, undefined, { shallow: true })
}

// TODO(rkofman): this likely belongs in lib/timestamp.ts; similar to
// `fromQueryParam` in lib/timestamp_range.ts
export function queryParamsToDate(
  dateParam: string | string[] | undefined
): Date | null {
  if (dateParam && typeof dateParam === "string") {
    const d = parseISO(decodeURIComponent(dateParam))
    if (!isValid(d)) {
      return null
    }
    return d
  }

  return null
}

// TODO(rkofman): this likely belongs in lib/timestamp.ts; similar to
// `toQueryParam` in lib/timestamp_range.ts
export function dateToQueryParams(d: Date | null): string {
  if (!d) {
    return ""
  }

  return encodeURIComponent(formatISO(d))
}

/**
 * @deprecated The URL has length limits, and URL encoding entire objects should
 * be avoided.  Instead, use an identifier.
 */
export function deprecatedQueryParamsToLocation(
  locationParam: string | string[] | undefined
): Location | null {
  if (locationParam && typeof locationParam === "string") {
    const decodedLocation = decodeURIComponent(locationParam)
    const parsedLocation = JSON.parse(decodedLocation)
    parsedLocation.id = fixParsedId(parsedLocation.id)
    return parsedLocation
  }

  return null
}

/**
 * @deprecated The URL has length limits, and URL encoding entire objects should
 * be avoided.  Instead, use an identifier.
 */
export function deprecatedLocationToQueryParams(
  location: Location | null
): string {
  if (!location) {
    return ""
  }

  return encodeURIComponent(stringify(location))
}

/**
 * @deprecated Use a URLSearchParams object to encode query params instead.
 * Wrapper around `Router.replace(...{shallow:true})` that removes empty params
 * and merges values with a comma.
 *
 * Note: parameter values are expected to be simple, and not include url-encoded
 * entities like commas.
 *
 * TODO(rkofman): refactor this method's behavior to combine with `pushParam`,
 * above. Currently blocked because `pushParam` is expected to work with complex
 * serialized data - an expectation we should remove before combining.
 *
 * @param query New query params. Lists will be joined with a comma `,`. e.g.
 *   `{foo: ["1", "two"]}` will become `"?foo=1,two"`
 * @returns If route change is cancelled by Next.js, the promise will resolve
 *   successfully but with a false value.
 */
export function deprecatedReplaceParams(
  query: Readonly<{ [key: string]: string | string[] }> = {}
): Promise<boolean> {
  const processedQuery = encode(deprecatedBuildQueryParams(query)).replaceAll(
    "%2C",
    ","
  )
  return Router.replace("?" + processedQuery, undefined, {
    shallow: true,
  })
}

/**
 * @deprecated Build query params with encodeQueryParam instead.
 */
export function deprecatedBuildQueryParams(
  query: Readonly<{ [key: string]: string | string[] }> = {}
): { [key: string]: string } {
  const result: { [key: string]: string } = {}

  for (const [key, value] of Object.entries(query)) {
    let processedValue: string

    if (Array.isArray(value)) {
      // Avoids `foo=1&foo=2` syntax in favor of `foo=1,2`
      processedValue = _(value)
        .filter((v) => !!v)
        .join(",")
    } else {
      processedValue = value
    }

    // Cleans up empty params to avoid extraneous `&foo=` or `&`
    if (processedValue) {
      result[key] = processedValue
    }
  }

  return result
}
